import { useEffect, useRef } from 'react';
import { useSocket } from '../context/WebsocketContext';

/**
 * Bulk-subscribes the websocket connection to every visible feed post so
 * INTERACTION_UPDATED / new_replies / STORY_SCORE_UPDATED broadcasts
 * actually reach the feed user.
 *
 * Background: the WS server stores subscriptions as one WATCHING# item
 * per (connection, post) pair. The post page subscribes to the single
 * post it's rendering on mount; the feed page never used to subscribe
 * to anything, which is why feed cards silently missed every real-time
 * count update before TRIL-43.
 *
 * Subscription set is diffed across renders so we only send the delta
 * (new posts → subscribe, removed posts → unsubscribe) instead of
 * thrashing the server every time the feed re-renders.
 */
export function useFeedPostSubscriptions(visiblePostIds: string[]): void {
  const { authenticated, sendMessage } = useSocket();
  const subscribedRef = useRef<Set<string>>(new Set());

  // sendMessage gets a fresh reference whenever the socket reconnects.
  // The unmount-time cleanup below runs in a useEffect with an empty
  // deps array, so it would otherwise close over the very first
  // sendMessage and try to unsubscribe through a now-closed socket —
  // leaving WATCHING# rows alive on the server until TTL. Mirroring
  // sendMessage into a ref keeps the cleanup honest.
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const authenticatedRef = useRef(authenticated);
  useEffect(() => {
    authenticatedRef.current = authenticated;
  }, [authenticated]);

  // Stable cache key — re-renders that produce the same id list don't
  // re-fire the effect. The set diff inside still handles real changes.
  const idsKey = visiblePostIds.join('|');

  useEffect(() => {
    if (!authenticated) {
      // On disconnect the server already wipes all WATCHING rows for
      // this connection (close handler). Reset our local view so the
      // next reconnect can re-subscribe from scratch.
      subscribedRef.current = new Set();
      return;
    }

    const desired = new Set(visiblePostIds.filter(Boolean));
    const current = subscribedRef.current;

    const toAdd: string[] = [];
    for (const id of desired) {
      if (!current.has(id)) toAdd.push(id);
    }
    const toRemove: string[] = [];
    for (const id of current) {
      if (!desired.has(id)) toRemove.push(id);
    }

    if (toAdd.length > 0) {
      sendMessage({ action: 'subscribeToPosts', postIds: toAdd });
    }
    if (toRemove.length > 0) {
      sendMessage({ action: 'unsubscribeFromPosts', postIds: toRemove });
    }
    subscribedRef.current = desired;
    // visiblePostIds intentionally omitted — idsKey captures its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, idsKey, sendMessage]);

  // On unmount (user navigates away from the feed) tear down every
  // subscription so we don't leak entries in PostWatchersIndex. Reads
  // from the refs above to avoid capturing a stale sendMessage from
  // the first render — important when the socket reconnects while the
  // feed is open.
  useEffect(() => {
    return () => {
      const current = subscribedRef.current;
      if (current.size === 0) return;
      // If the socket isn't currently authenticated the server's close
      // handler has already wiped this connection's WATCHING# rows; the
      // unsubscribe message would be a no-op (or worse, fire on a new
      // socket and unsubscribe from a fresh state). Skip it.
      if (!authenticatedRef.current) {
        subscribedRef.current = new Set();
        return;
      }
      sendMessageRef.current({
        action: 'unsubscribeFromPosts',
        postIds: Array.from(current),
      });
      subscribedRef.current = new Set();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
